/**
 * Core tool types reused as an option on a Primary / Secondary tool type.
 *
 * A Primary type (e.g. Contracts) can offer "whatever the client runs for a
 * Core tool" (e.g. their CRM) as one of its choices. The link lives on the
 * type doc:
 *
 *   technologyStack/{Primary|Secondary}/types/{typeId}.coreTools = [
 *     { coreTypeId, useCaseIds: [], suggested: false }
 *   ]
 *
 * The option resolves to the client's current pick for that Core tool (so a
 * client on JobTread for CRM sees JobTread under Contracts), else the
 * template's suggested software for it. useCaseIds index into THIS type's
 * useCases and suggested is per type. Selections of it carry
 * linkedCoreTypeId so they keep following the Core pick.
 * functions/mcp/techStack.js mirrors this.
 */

export const CORE_LINKS_FIELD = 'coreTools';
// Oct 27 2026 interim shape linked single Core software; each kept its Core tool.
const LEGACY_CORE_LINKS_FIELD = 'coreSoftware';

export const coreLinkOptionId = (coreTypeId) => `core-tool-${coreTypeId}`;

/** Normalised Core tool links from a type doc (one per Core tool). */
export const getCoreToolLinks = (typeData) => {
  const raw = Array.isArray(typeData?.[CORE_LINKS_FIELD])
    ? typeData[CORE_LINKS_FIELD]
    : Array.isArray(typeData?.[LEGACY_CORE_LINKS_FIELD])
      ? typeData[LEGACY_CORE_LINKS_FIELD]
      : [];
  const seen = new Set();
  return raw
    .filter((link) => link && link.coreTypeId && !seen.has(String(link.coreTypeId)) && seen.add(String(link.coreTypeId)))
    .map((link) => ({
      coreTypeId: String(link.coreTypeId),
      useCaseIds: Array.isArray(link.useCaseIds) ? link.useCaseIds.map(String) : [],
      suggested: !!link.suggested,
    }));
};

/**
 * What a Core tool currently stands for: the client's pick (an explicit
 * "None" means nothing), else the template's suggested software. `coreType`
 * is { name, software: [] }; returns null when nothing resolves.
 */
export const resolveCoreToolPick = (coreType, selection) => {
  if (selection && selection.name) {
    if (selection.name === 'None' || selection.name === '__CUSTOM__') return null;
    const lib = (coreType.software || []).find((s) => s.name === selection.name);
    return {
      name: selection.name,
      image: lib?.image || selection.image || '',
      signupUrl: lib?.signupUrl || selection.signupUrl || '',
      open: lib ? !!lib.open : !!selection.open,
      source: 'client',
    };
  }
  const suggested = (coreType.software || []).find((s) => s.suggested);
  if (!suggested) return null;
  return { name: suggested.name, image: suggested.image || '', signupUrl: suggested.signupUrl || '', open: !!suggested.open, source: 'suggested' };
};

/**
 * Options for a type's Core tool links, shaped like the type's own software.
 * `coreTypesById` maps Core typeId → { name, software: [] }; `selections` is
 * the client's selectedSoftware (empty on the template). A link whose name
 * the type already offers as its own option is dropped (selections match by name).
 */
export const resolveCoreToolLinks = (typeData, coreTypesById, selections = [], ownSoftware = []) => {
  const taken = new Set(ownSoftware.map((s) => String(s.name || '').toLowerCase()));
  const out = [];
  getCoreToolLinks(typeData).forEach((link) => {
    const coreType = coreTypesById?.get ? coreTypesById.get(link.coreTypeId) : coreTypesById?.[link.coreTypeId];
    if (!coreType) return;
    const selection = (selections || []).find((s) => s.categoryId === 'core' && s.toolId === link.coreTypeId);
    const pick = resolveCoreToolPick(coreType, selection);
    const name = pick?.name || `Your ${coreType.name}`;
    if (taken.has(name.toLowerCase())) return;
    taken.add(name.toLowerCase());
    out.push({
      id: coreLinkOptionId(link.coreTypeId),
      name,
      image: pick?.image || '',
      signupUrl: pick?.signupUrl || '',
      open: !!pick?.open,
      useCaseIds: link.useCaseIds,
      suggested: link.suggested,
      fromCore: true,
      linkedCoreTypeId: link.coreTypeId,
      coreTypeName: coreType.name,
    });
  });
  return out;
};

/** Display name for a saved selection: a linked one follows the current Core pick. */
export const selectionDisplayName = (selection, availableSoftware = []) => {
  if (!selection) return '';
  if (selection.linkedCoreTypeId) {
    const linked = availableSoftware.find((s) => s.linkedCoreTypeId === selection.linkedCoreTypeId);
    if (linked) return linked.name;
  }
  return selection.name;
};
